/*
 * MappingGridContainer
 *
 * Container component that manages all three mapping grids (rates field, rates fixing,
 * legal entity) and shows only the active one based on the current tab.
 * In edit mode, when editModeSnapshot is set, shows only the snapshot rows in order (no filter/sort).
 */

import { useSelector } from 'react-redux';
import { TABS, SCREEN_MODES, Tab } from '../constants';
import {
  MappingAdminState,
  FieldMappingDataRow,
  FixingProxyMappingDataRow,
  LegalEntityMappingDataRow,
} from '../../../common/domain/mappingAdmin';
import { MappingDataRow } from '../interfaces';
import MappingDataGrid from './MappingDataGrid';
import { MappingGridContainerProps } from '../interfaces';

function getCurrentRowData(
  id: string,
  tabType: Tab,
  state: MappingAdminState,
):
  | FieldMappingDataRow
  | FixingProxyMappingDataRow
  | LegalEntityMappingDataRow
  | undefined {
  const { ratesData, draftRow, editedRows, addedRows, deletedRows } = state;
  const tabRates = ratesData[tabType] ?? {};

  if (draftRow?.data.id === id) return draftRow.data as any;
  if (deletedRows[id]) return deletedRows[id];
  if (editedRows[id]) return editedRows[id].newData;
  if (addedRows[id]) return addedRows[id];
  return tabRates[id];
}

const MappingGridContainer = ({
  username,
  dispatch,
  onGridReady,
}: MappingGridContainerProps) => {
  const mappingAdminState = useSelector(
    (state: any) => state.mappingAdmin as MappingAdminState,
  );
  const {
    ratesData,
    draftRow,
    activeTab,
    screenMode,
    editModeSnapshot,
  } = mappingAdminState;

  const useSnapshot =
    screenMode === SCREEN_MODES.EDITING_MODE && editModeSnapshot != null;

  const isNew = (rowId: string) => rowId.startsWith('temp_');

  const getTabRowData = (tabType: Tab): MappingDataRow[] => {
    if (useSnapshot) {
      return (editModeSnapshot?.[tabType] ?? [])
        .map((id) => getCurrentRowData(id, tabType, mappingAdminState))
        .filter(Boolean) as MappingDataRow[];
    }
    const rows = { ...ratesData[tabType] };
    if (draftRow?.tabType === tabType) {
      rows[draftRow.data.id] = draftRow.data;
    }
    return Object.values(rows).sort((a: any, b: any) => {
      if (isNew(a.id) && isNew(b.id)) {
        return (
          new Date(b.lastUpdateTimestamp).getTime() -
          new Date(a.lastUpdateTimestamp).getTime()
        );
      }
      return isNew(a.id) ? -1 : 1;
    }) as MappingDataRow[];
  };

  return (
    <>
      {(Object.values(TABS) as Tab[]).map((tabType) => (
        <div
          key={tabType}
          style={{ display: activeTab === tabType ? 'block' : 'none' }}
        >
          <MappingDataGrid
            rowData={getTabRowData(tabType)}
            username={username}
            screenMode={screenMode}
            tabType={tabType}
            activeTab={activeTab}
            dispatch={dispatch}
            onGridReady={(api) => onGridReady?.(tabType, api)}
          />
        </div>
      ))}
    </>
  );
};

export default MappingGridContainer;
